<template>
  <div v-if="item.is_public" class="cd-feedbacks-wrapper">

    <!-- New Comment -->
    <div class="cd-feedback-form">
      <div class="cd-feedback-avatar">
        <img :src="userPhoto" alt="" />
      </div>
      <div class="cd-feedback-input-wrapper">
        <textarea :value="commentText" :placeholder="$t('general.write_your_experiences_here')"
                  class="cd-feedback-textarea" rows="3"
                  @input="$emit('update:commentText', $event.target.value)"></textarea>
        <div class="cd-feedback-actions">
          <button class="cd-btn cd-btn--primary cd-btn--sm" type="button" @click="$emit('submit')">
            <i class="uil-message"></i>
            {{ $t("messages.send") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Comments List -->
    <div class="cd-feedback-list">
      <div v-for="(comment, index) in comments" :key="'comment_index_' + index"
           :id="'comment_' + comment.id" class="cd-feedback-item">
        <div class="cd-feedback-avatar">
          <img :src="comment.user?.photo_link || comment.user?.photo || '/assets/images/default-avatar.png'" alt="" />
        </div>
        <div class="cd-feedback-content">
          <div class="cd-feedback-header">
            <span class="cd-feedback-name">{{ comment.user?.name }} {{ comment.user?.surname }}</span>
            <span v-if="comment.user?.position?.name" class="cd-feedback-position">{{ comment.user.position.name }}</span>
          </div>
          <p class="cd-feedback-text">
            {{ comment.text }}
            <span v-if="comment.status_id === 1" class="cd-feedback-pending">
              ({{ $t('general.sent_to_admin_approval') }})
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CollectionFeedbacks",
  props: {
    item: { type: Object, required: true },
    comments: { type: Array, default: () => [] },
    userPhoto: { type: String, default: '' },
    commentText: { type: String, default: '' },
  },
  emits: ['update:commentText', 'submit'],
};
</script>

<style scoped>
/* Wrapper */
.cd-feedbacks-wrapper {
  /* Padding removed - now handled by tab content */
}

/* Feedback List */
.cd-feedback-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Scrollbar styling */
.cd-feedback-list::-webkit-scrollbar {
  width: 6px;
}

.cd-feedback-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.cd-feedback-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.cd-feedback-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.cd-feedback-item {
  display: flex;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.cd-feedback-avatar {
  flex-shrink: 0;
}

.cd-feedback-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.cd-feedback-content {
  flex: 1;
  min-width: 0;
}

.cd-feedback-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.cd-feedback-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.cd-feedback-position {
  font-size: 12px;
  color: #64748b;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.cd-feedback-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  word-break: break-word;
}

.cd-feedback-pending {
  font-weight: 600;
  color: #d97706;
  font-size: 13px;
}

/* New Comment Form */
.cd-feedback-form {
  display: flex;
  gap: 14px;
  padding: 20px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.cd-feedback-input-wrapper {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.cd-feedback-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  resize: vertical;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  line-height: 1.5;
}

.cd-feedback-textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  background: #fff;
}

.cd-feedback-textarea::placeholder {
  color: #94a3b8;
}

.cd-feedback-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.cd-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cd-btn--primary {
  background: #2563eb;
  color: #fff;
}

.cd-btn--primary:hover {
  background: #1d4ed8;
}

.cd-btn--sm {
  padding: 6px 12px;
  font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .cd-feedback-item {
    padding: 12px;
    gap: 10px;
  }

  .cd-feedback-avatar img {
    width: 36px;
    height: 36px;
  }

  .cd-feedback-form {
    padding: 14px;
    gap: 10px;
  }

  .cd-feedback-textarea {
    padding: 10px 12px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .cd-feedback-form {
    flex-direction: column;
    align-items: flex-start;
  }

  .cd-feedback-avatar img {
    width: 32px;
    height: 32px;
  }

  .cd-feedback-actions {
    width: 100%;
  }

  .cd-feedback-actions .cd-btn {
    width: 100%;
  }
}
</style>
